import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import DOMPurify from "dompurify";
import { Avatar, Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { array_status } from "../../ReduxTLK/features/SAV/ticketList";
import { splitLinks } from "./chatText";
import { AttachmentCard, AttachmentPreviewDialog } from "./ChatAttachments";
import {
  avatarColor,
  buildTimeline,
  displayName,
  formatDayLabel,
  formatFullDate,
  formatRelative,
  formatTime,
  initials,
} from "./chatTimeline";

const FONT = '"DM Sans", sans-serif';
const AVATAR_SIZE = 28;
const INCOMING_INDENT = `${AVATAR_SIZE + 8}px`;

// Re-renders every `intervalMs` so "il y a x min" stays accurate while the chat is open
function useNow(intervalMs) {
  const [now, setNow] = useState(() => moment());
  useEffect(() => {
    if (!intervalMs) return undefined;
    setNow(moment());
    const timer = setInterval(() => setNow(moment()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
}

// Message text with clickable links (line breaks are kept by white-space: pre-wrap)
const MessageText = ({ text }) => (
  <>
    {splitLinks(text).map((part, index) =>
      index % 2 === 1 ? (
        <a key={index} href={part} target="_blank" rel="noopener noreferrer" style={{ color: '#1671D9', textDecoration: 'underline' }}>
          {part}
        </a>
      ) : (
        <React.Fragment key={index}>{part}</React.Fragment>
      )
    )}
  </>
);

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // clipboard blocked by the browser: the text can still be selected manually
    }
  };

  return (
    <Tooltip title={copied ? 'Copié !' : 'Copier le message'} placement="top">
      <IconButton
        size="small"
        onClick={copy}
        aria-label="Copier le message"
        className="chat-message-actions"
        sx={{ flexShrink: 0, opacity: copied ? 1 : 0, transition: 'opacity .15s ease', color: '#98A2B3' }}
      >
        {copied ? <CheckRoundedIcon sx={{ fontSize: 16, color: '#079455' }} /> : <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />}
      </IconButton>
    </Tooltip>
  );
}

function MessageTime({ date, now }) {
  return (
    <Tooltip title={formatFullDate(date)} placement="top">
      <Typography component="span" sx={{ fontFamily: FONT, fontSize: '12px', color: '#667085', whiteSpace: 'nowrap', cursor: 'default' }}>
        {formatTime(date)}
        <Box component="span" sx={{ color: '#98A2B3' }}> · {formatRelative(date, now)}</Box>
      </Typography>
    </Tooltip>
  );
}

function DaySeparator({ date, now }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2, mb: 0.5 }}>
      <Box sx={{ flex: 1, height: '1px', bgcolor: '#EAECF0' }} />
      <Typography
        sx={{ fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: '#667085', px: 1.25, py: 0.25, borderRadius: 999, bgcolor: '#F2F4F7' }}
      >
        {formatDayLabel(date, now)}
      </Typography>
      <Box sx={{ flex: 1, height: '1px', bgcolor: '#EAECF0' }} />
    </Box>
  );
}

function GroupHeader({ message, isOwn, now }) {
  const color = avatarColor(message.sender?.id ?? message.sender?.nom);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: isOwn ? 'flex-end' : 'flex-start', mb: 0.5, minWidth: 0 }}>
      {!isOwn && (
        <Avatar sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE, fontSize: '12px', fontWeight: 700, bgcolor: color.bg, color: color.fg }}>
          {initials(message.sender?.nom)}
        </Avatar>
      )}
      <Typography noWrap sx={{ fontFamily: FONT, fontSize: '13px', fontWeight: 600, color: '#344054', minWidth: 0 }}>
        {isOwn ? 'Vous' : displayName(message.sender?.nom)}
      </Typography>
      <MessageTime date={message.date} now={now} />
    </Box>
  );
}

function TextBubble({ text, isOwn, startsGroup }) {
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexDirection: isOwn ? 'row-reverse' : 'row',
        '&:hover .chat-message-actions, & .chat-message-actions:focus-visible': { opacity: 1 },
      }}
    >
      <Box
        sx={{
          maxWidth: '85%',
          px: 1.5,
          py: 1,
          fontFamily: FONT,
          fontSize: '14px',
          lineHeight: '21px',
          color: '#101828',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          bgcolor: isOwn ? '#E3EFFD' : '#FFFFFF',
          border: '1px solid',
          borderColor: isOwn ? '#C9DEF8' : '#EAECF0',
          borderRadius: '14px',
          [isOwn ? 'borderTopRightRadius' : 'borderTopLeftRadius']: startsGroup ? '4px' : '14px',
        }}
      >
        <MessageText text={text} />
      </Box>
      <CopyButton text={text} />
    </Box>
  );
}

function StatusChip({ status }) {
  return (
    <Box
      component="span"
      sx={{ display: 'inline-block', px: 0.75, borderRadius: '6px', fontWeight: 600, whiteSpace: 'nowrap', color: status.backgroundColor, bgcolor: `${status.backgroundColor}14` }}
    >
      {status.nom}
    </Box>
  );
}

function StatusEvent({ message, causes, now }) {
  const update = message.messageUpdateStatut;
  const oldStatus = array_status.find((status) => status.id === update?.action?.firstStatuts);
  const newStatus = array_status.find((status) => status.id === update?.action?.secondStatuts);
  const cause = causes?.find((item) => item.valeur === update?.cause)?.text;
  const note = update?.note;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
      <Box
        sx={{
          width: '92%',
          px: 1.5,
          py: 1.25,
          borderRadius: '12px',
          bgcolor: '#FFFFFF',
          border: '1px dashed #D0D5DD',
          fontFamily: FONT,
          fontSize: '12.5px',
          lineHeight: '20px',
          color: '#475467',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <SyncAltRoundedIcon sx={{ fontSize: 16, color: '#667085', mt: '2px' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box component="span" sx={{ fontWeight: 600, color: '#344054' }}>{displayName(message.sender?.nom)}</Box>
            {' '}a changé le statut du ticket
            {oldStatus && <> de <StatusChip status={oldStatus} /></>}
            {newStatus && <> vers <StatusChip status={newStatus} /></>}
            {cause && (
              <Box sx={{ mt: 0.75 }}>
                <Box component="span" sx={{ fontWeight: 600, color: '#344054' }}>Cause : </Box>{cause}
              </Box>
            )}
            {note && (
              <Box sx={{ mt: 0.75, '& p': { m: 0 } }}>
                <Box component="span" sx={{ fontWeight: 600, color: '#344054' }}>Note : </Box>
                <Box component="span" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note) }} />
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ mt: 0.5, textAlign: 'right' }}>
          <MessageTime date={message.date} now={now} />
        </Box>
      </Box>
    </Box>
  );
}

function EmptyState() {
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, px: 3, textAlign: 'center' }}>
      <Box sx={{ width: 48, height: 48, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: '#E8F1FC', color: '#1671D9' }}>
        <ForumOutlinedIcon />
      </Box>
      <Typography sx={{ fontFamily: FONT, fontSize: '14px', fontWeight: 600, color: '#344054' }}>Aucun message pour le moment</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: '#667085' }}>Les échanges avec le client apparaîtront ici.</Typography>
    </Box>
  );
}

export default function ChatMessageList({ messages, userId, causes, active }) {
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  // "sticky bottom": follow new content until the agent scrolls up to read older messages
  const stickRef = useRef(true);
  const lastScrollTop = useRef(0);
  const previousCount = useRef(0);
  const [showJump, setShowJump] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const [preview, setPreview] = useState({ attachment: null, open: false });
  const now = useNow(active ? 30000 : null);

  const items = useMemo(() => buildTimeline(messages, userId), [messages, userId]);
  const lastItem = items[items.length - 1];
  const lastIsOwn = lastItem?.type === 'message' && lastItem.isOwn;

  const scrollToBottom = useCallback(() => {
    const list = scrollRef.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight;
    lastScrollTop.current = list.scrollTop;
    stickRef.current = true;
    setShowJump(false);
    setHasNew(false);
  }, []);

  // New messages: follow them if the agent is at the bottom (or just sent one), otherwise show a button
  useLayoutEffect(() => {
    const added = items.length > previousCount.current;
    previousCount.current = items.length;
    if (!added) return;
    if (stickRef.current || lastIsOwn) scrollToBottom();
    else setHasNew(true);
  }, [items.length, lastIsOwn, scrollToBottom]);

  // Sizes change without new messages: composer growing with a long draft, fonts loading, audio player opening…
  useEffect(() => {
    const list = scrollRef.current;
    const content = contentRef.current;
    if (!list || !content || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      // a scroll up not yet reported by a scroll event must win over re-pinning
      if (stickRef.current && list.scrollTop >= lastScrollTop.current - 1) scrollToBottom();
    });
    observer.observe(list);
    observer.observe(content);
    return () => observer.disconnect();
  }, [scrollToBottom]);

  useEffect(() => {
    if (!active) return undefined;
    const frame = requestAnimationFrame(scrollToBottom);
    return () => cancelAnimationFrame(frame);
  }, [active, scrollToBottom]);

  const handleScroll = () => {
    const list = scrollRef.current;
    const atBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 60;
    // only a real scroll up unsticks the list (layout changes don't move scrollTop up)
    if (atBottom) stickRef.current = true;
    else if (list.scrollTop < lastScrollTop.current) stickRef.current = false;
    lastScrollTop.current = list.scrollTop;
    setShowJump(!stickRef.current);
    if (stickRef.current) setHasNew(false);
  };

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, bgcolor: '#F9FAFB' }}>
      <Box ref={scrollRef} onScroll={handleScroll} role="log" sx={{ height: '100%', overflowY: 'auto', px: 2 }}>
        <Box ref={contentRef} sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column', pb: 2 }}>
        {items.length === 0 ? <EmptyState /> : items.map((item) => {
          if (item.type === 'day') return <DaySeparator key={item.key} date={item.date} now={now} />;
          if (item.type === 'status') return <StatusEvent key={item.key} message={item.message} causes={causes} now={now} />;

          const { message, isOwn, startsGroup } = item;
          const attachment = message.attachement?.id ? message.attachement : null;
          return (
            <Box key={item.key} sx={{ mt: startsGroup ? 1.5 : 0.5 }}>
              {startsGroup && <GroupHeader message={message} isOwn={isOwn} now={now} />}
              <Box sx={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start', pl: isOwn ? 0 : INCOMING_INDENT }}>
                {attachment ? (
                  <AttachmentCard
                    attachment={attachment}
                    isOwn={isOwn}
                    onPreview={(selected) => setPreview({ attachment: selected, open: true })}
                  />
                ) : (
                  <TextBubble text={message.contenu} isOwn={isOwn} startsGroup={startsGroup} />
                )}
              </Box>
            </Box>
          );
        })}
        </Box>
      </Box>

      {showJump && (
        <Button
          size="small"
          onClick={scrollToBottom}
          startIcon={<KeyboardArrowDownRoundedIcon />}
          sx={{
            position: 'absolute',
            left: '50%',
            bottom: 12,
            transform: 'translateX(-50%)',
            px: 1.5,
            borderRadius: 999,
            textTransform: 'none',
            fontFamily: FONT,
            fontWeight: 600,
            color: hasNew ? '#FFFFFF' : '#344054',
            bgcolor: hasNew ? '#1671D9' : '#FFFFFF',
            boxShadow: '0 4px 12px rgba(16, 24, 40, 0.15)',
            '&:hover': { bgcolor: hasNew ? '#125FB8' : '#F2F4F7' },
          }}
        >
          {hasNew ? 'Nouveaux messages' : 'Derniers messages'}
        </Button>
      )}

      <AttachmentPreviewDialog
        attachment={preview.attachment}
        open={preview.open}
        onClose={() => setPreview((current) => ({ ...current, open: false }))}
      />
    </Box>
  );
}
